<script setup lang="ts">
// The shared pager (contract 02 §8; rebuild of legacy `OrphexPagination.vue`, analysis §2.5).
// Rebuilt to remove three couplings the legacy carried:
//   • the `format.js` import (`:64`) → the injected `useFormat()` funnel (counts are Intl-grouped);
//   • the hardcoded `"of"`/`"…"` (`:53`, `:32`) → the `common.pagination.range` ICU key via `t()`;
//   • the global `o-soft-btn`/`o-custom-select` composition → the `@/shared/ui` Button + Select.
// It also FIXES legacy defect #3 (`:22–30`): the active-page predicate is `page === selectedPage`
// (legacy tested the always-truthy `selectedPage`, so every page rendered primary and the current
// page was indistinguishable). Only the active page renders `primary`; the rest render `soft`.
import { computed, ref, watch } from 'vue'

import type { PaginationChange, PaginationProps } from './types'

import { i18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Button } from '@/shared/ui/button'
import { Select } from '@/shared/ui/select'
import type { SelectOption } from '@/shared/ui/select'

const props = withDefaults(defineProps<PaginationProps>(), {
  first: 0,
  pageSizes: () => [10, 25, 50, 100],
  firstLabel: 'ui.pagination.first',
  prevLabel: 'ui.pagination.prev',
  nextLabel: 'ui.pagination.next',
  lastLabel: 'ui.pagination.last',
})

const emit = defineEmits<{ change: [payload: PaginationChange] }>()

const fmt = useFormat()

/** Ellipsis sentinel in the page window — a non-numeric slot renders the gap glyph. */
const ELLIPSIS = '…'

/** Decorative boundary/step glyphs. Accessible names come from the `*Label` props, not these. */
const NAV_GLYPH = { first: '«', prev: '‹', next: '›', last: '»' } as const

function validRows(value: number | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : null
}

function validTotal(value: number): number {
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0
}

function pageCount(total: number, rows: number): number {
  return Math.max(1, Math.ceil(validTotal(total) / rows))
}

function validFirst(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0
}

function clampPage(page: number, total: number, rows: number): number {
  return Math.max(1, Math.min(page, pageCount(total, rows)))
}

const configuredPageSizes = computed(() => {
  const unique = new Set<number>()
  for (const value of props.pageSizes) {
    const normalized = validRows(value)
    if (normalized !== null) unique.add(normalized)
  }
  const controlledRows = validRows(props.rows)
  if (controlledRows !== null) unique.add(controlledRows)
  return [...unique]
})

const selectedRows = ref<number>(validRows(props.rows) ?? configuredPageSizes.value[0] ?? 10)
const selectedPage = ref(
  clampPage(
    Math.floor(validFirst(props.first) / selectedRows.value) + 1,
    props.total,
    selectedRows.value,
  ),
)
const normalizedTotal = computed(() => validTotal(props.total))

const totalPages = computed(() => pageCount(normalizedTotal.value, selectedRows.value))

/** 1 … window … last, matching the legacy window (parity: `OrphexPagination.vue:78–102`). */
const pages = computed<(number | string)[]>(() => {
  const tp = totalPages.value
  const cur = selectedPage.value
  if (tp <= 7) return Array.from({ length: tp }, (_, index) => index + 1)

  const out: (number | string)[] = [1]
  if (cur > 4) out.push(ELLIPSIS)
  const start = Math.max(2, cur - 2)
  const end = Math.min(tp - 1, cur + 2)
  for (let page = start; page <= end; page++) out.push(page)
  if (cur < tp - 3) out.push(ELLIPSIS)
  out.push(tp)
  return out
})

const startRow = computed(() => (selectedPage.value - 1) * selectedRows.value)
const endRow = computed(() =>
  Math.min(selectedPage.value * selectedRows.value, normalizedTotal.value),
)
const firstDisplayedRow = computed(() => (normalizedTotal.value === 0 ? 0 : startRow.value + 1))

/** Range copy through the funnel + ICU key — no concatenation, no `toFixed` (analysis §2.5). */
const rangeText = computed(() =>
  i18n.global.t('common.pagination.range', {
    start: fmt.formatInteger(firstDisplayedRow.value),
    end: fmt.formatInteger(endRow.value),
    total: fmt.formatInteger(normalizedTotal.value),
  }),
)

const rowsOptions = computed<SelectOption<number>[]>(() =>
  configuredPageSizes.value.map((size) => ({ value: size, label: fmt.formatInteger(size) })),
)

function onPaginate(page: number): void {
  selectedPage.value = Math.max(1, Math.min(page, totalPages.value))
  emit('change', {
    first: (selectedPage.value - 1) * selectedRows.value,
    rows: selectedRows.value,
  })
}

function onRowsChange(value: number | null): void {
  const normalized = validRows(value ?? undefined)
  if (normalized === null || normalized === selectedRows.value) return
  selectedRows.value = normalized
  onPaginate(Math.min(selectedPage.value, totalPages.value))
}

// Re-clamp the current page when the record count shrinks it out of range
// (parity: `OrphexPagination.vue:115–118`).
watch(
  () => props.total,
  () => {
    onPaginate(Math.min(selectedPage.value, totalPages.value))
  },
)

// A controlled page-size value drives the selector and page calculation. The existing `change`
// event remains the only outward event, so callers that do not pass `rows` keep legacy behaviour.
watch(
  () => props.rows,
  (rows) => {
    const normalized = validRows(rows)
    if (normalized === null || normalized === selectedRows.value) return
    selectedRows.value = normalized
    selectedPage.value = clampPage(
      Math.floor(validFirst(props.first) / normalized) + 1,
      props.total,
      normalized,
    )
  },
)

// An external `first` offset drives the page (parity: `OrphexPagination.vue:120–129`).
watch(
  () => props.first,
  (first) => {
    const targetPage = Math.floor(validFirst(first) / selectedRows.value) + 1
    if (targetPage !== selectedPage.value) onPaginate(targetPage)
  },
)
</script>

<template>
  <div class="ui-pagination flex items-center justify-between gap-sm font-sans">
    <div class="ui-pagination__pages flex items-center gap-2xs">
      <Button
        class="rounded-full"
        variant="soft"
        size="sm"
        square
        :disabled="selectedPage === 1"
        :aria-label="firstLabel"
        @click="onPaginate(1)"
      >
        {{ NAV_GLYPH.first }}
      </Button>
      <Button
        class="rounded-full"
        variant="soft"
        size="sm"
        square
        :disabled="selectedPage === 1"
        :aria-label="prevLabel"
        @click="onPaginate(selectedPage - 1)"
      >
        {{ NAV_GLYPH.prev }}
      </Button>

      <template v-for="(page, index) in pages" :key="`${page}-${index}`">
        <Button
          v-if="typeof page === 'number'"
          class="rounded-full"
          :variant="page === selectedPage ? 'primary' : 'soft'"
          size="sm"
          square
          :aria-current="page === selectedPage ? 'page' : undefined"
          @click="onPaginate(page)"
        >
          {{ page }}
        </Button>
        <span
          v-else
          class="ui-pagination__ellipsis inline-flex h-8 w-8 select-none items-center justify-center text-muted"
          aria-hidden="true"
        >
          …
        </span>
      </template>

      <Button
        class="rounded-full"
        variant="soft"
        size="sm"
        square
        :disabled="selectedPage === totalPages"
        :aria-label="nextLabel"
        @click="onPaginate(selectedPage + 1)"
      >
        {{ NAV_GLYPH.next }}
      </Button>
      <Button
        class="rounded-full"
        variant="soft"
        size="sm"
        square
        :disabled="selectedPage === totalPages"
        :aria-label="lastLabel"
        @click="onPaginate(totalPages)"
      >
        {{ NAV_GLYPH.last }}
      </Button>
    </div>

    <div class="ui-pagination__meta flex items-center justify-end gap-sm">
      <p class="ui-pagination__range whitespace-nowrap text-xs text-fg">{{ rangeText }}</p>
      <div
        v-if="rowsOptions.length"
        class="ui-pagination__rows w-20"
        role="group"
        :aria-label="rowsLabel"
      >
        <Select
          :model-value="selectedRows"
          :options="rowsOptions"
          size="sm"
          @change="onRowsChange"
        />
      </div>
    </div>
  </div>
</template>
